import React from 'react';
import { Alert, Form } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TransportFormSection from './TransportFormSection';
import TransportAddressListField from './TransportAddressListField';
import { TRANSPORT_DATE_DISPLAY_FORMAT } from './transportFormUtils';
import TransportDateInput from './TransportDateInput';
import './TransportRouteSection.css';

interface TransportRouteSectionProps {
  /** Section number (owner 2026-10-10: „Gdzie jedzie” shifts the following sections). */
  step?: number;
  onLoadingTouched: () => void;
  onDeliveryTouched: () => void;
  /** The operator picked the planned delivery date (stops the loading-date default). */
  onEstimatedDeliveryTouched?: () => void;
  /** New truck: the planned delivery follows the loading date (hint shown). */
  estimatedDeliveryFollowsLoading?: boolean;
  /** Per-order route points, rendered at the end of the same panel. */
  routePoints?: React.ReactNode;
}

/**
 * „Trasa i terminy” (owner 2026-10-10: „ładnie, jak na 2026 rok”): the trip
 * as two stops A → B joined by the route with the days in transit. Each stop
 * has its dates on top and its numbered places below. Field names, labels and
 * the address-list classes stay as before (form payload, e2e).
 */
const TransportRouteSection: React.FC<TransportRouteSectionProps> = ({
  step = 2,
  onLoadingTouched,
  onDeliveryTouched,
  onEstimatedDeliveryTouched,
  estimatedDeliveryFollowsLoading = false,
  routePoints,
}) => {
  const { t } = useTranslation(['transport']);
  const status = Form.useWatch('status');
  const actualDeliveryRequired = status === 'DELIVERED';
  const loadingDate = Form.useWatch('loadingDate');
  const estimatedDelivery = Form.useWatch('estimatedDelivery');
  const formatSummaryDate = (value: unknown) => (dayjs.isDayjs(value) ? value.format(TRANSPORT_DATE_DISPLAY_FORMAT) : null);
  const summaryDates = [formatSummaryDate(loadingDate), formatSummaryDate(estimatedDelivery)].filter(Boolean);
  const summary = summaryDates.length > 0 ? summaryDates.join(' → ') : undefined;
  const transitDays = dayjs.isDayjs(loadingDate) && dayjs.isDayjs(estimatedDelivery)
    ? estimatedDelivery.startOf('day').diff(loadingDate.startOf('day'), 'day')
    : null;

  return (
    <TransportFormSection sectionKey="route" step={step} icon={<EnvironmentOutlined />} label={t('transport:sections.routeSchedule')} summary={summary}>
      <div className="transport-journey" data-testid="transport-journey">
        <section className="transport-journey__stop transport-journey__stop--loading">
          <header className="transport-journey__head">
            <span className="transport-journey__pin" aria-hidden="true">A</span>
            <div>
              <div className="transport-journey__title">{t('transport:journey.loading')}</div>
              <div className="transport-journey__sub">{t('transport:journey.loadingSub')}</div>
            </div>
          </header>
          <div className="transport-journey__dates">
            <Form.Item
              name="loadingDate"
              label={t('transport:fields.loadingDate')}
              rules={[{ required: true, message: t('transport:validation.loadingDateRequired') }]}
            >
              <TransportDateInput placeholder={t('transport:placeholders.selectDate')} />
            </Form.Item>
          </div>
          {/* Any interaction with the loading list marks it user-owned, so the
              create-mode prefill from selected orders stops overwriting it. */}
          <div onInput={onLoadingTouched} onClickCapture={onLoadingTouched}>
            <TransportAddressListField
              name="loadingAddresses"
              variant="loading"
              compact
              label={t('transport:fields.loadingAddresses')}
              placeholder={t('transport:placeholders.addressLine')}
              errorMessage={t('transport:validation.loadingAddressRequired')}
            />
          </div>
        </section>

        <div className="transport-journey__link" aria-hidden="true">
          <span className="transport-journey__line" />
          <span className="transport-journey__chip" data-testid="transport-journey-transit">
            {transitDays === 0
              ? t('transport:journey.sameDay')
              : transitDays !== null && transitDays > 0
                ? t('transport:journey.transitDays', { count: transitDays })
                : t('transport:journey.transitUnknown')}
          </span>
          <span className="transport-journey__line" />
        </div>

        <section className="transport-journey__stop transport-journey__stop--delivery">
          <header className="transport-journey__head">
            <span className="transport-journey__pin transport-journey__pin--end" aria-hidden="true">B</span>
            <div>
              <div className="transport-journey__title">{t('transport:journey.delivery')}</div>
              <div className="transport-journey__sub">{t('transport:journey.deliverySub')}</div>
            </div>
          </header>
          <div className="transport-journey__dates transport-journey__dates--two">
            {/* Optional, defaults to the loading date (never blocks the save). */}
            <Form.Item
              name="estimatedDelivery"
              label={t('transport:fields.estimatedDelivery')}
              extra={estimatedDeliveryFollowsLoading || !estimatedDelivery
                ? t('transport:fields.estimatedDeliveryHint')
                : undefined}
            >
              <TransportDateInput
                placeholder={t('transport:placeholders.selectDate')}
                onChange={() => onEstimatedDeliveryTouched?.()}
              />
            </Form.Item>
            <Form.Item
              name="actualDelivery"
              label={t('transport:fields.actualDelivery')}
              rules={actualDeliveryRequired
                ? [{ required: true, message: t('transport:validation.actualDeliveryRequired') }]
                : []}
            >
              <TransportDateInput placeholder={t('transport:placeholders.selectDate')} />
            </Form.Item>
          </div>
          {actualDeliveryRequired ? (
            <Alert type="info" showIcon message={t('transport:actualDeliveryRequiredHint')} style={{ marginBottom: 12 }} />
          ) : null}
          <div onInput={onDeliveryTouched} onClickCapture={onDeliveryTouched}>
            <TransportAddressListField
              name="deliveryAddresses"
              variant="delivery"
              compact
              label={t('transport:fields.deliveryAddresses')}
              placeholder={t('transport:placeholders.addressLine')}
              errorMessage={t('transport:validation.deliveryAddressRequired')}
            />
          </div>
        </section>
      </div>
      {routePoints}
    </TransportFormSection>
  );
};

export default TransportRouteSection;
